import { useGetRegistryByUniqueCode } from "@/queries/digital-files/registries.api";
import type { AssociatedRegistryType } from "@/schemas/digital-files";
import {
  Avatar,
  AvatarFallback,
  Button,
  GroupTextField,
  Icon,
  Input,
} from "@elaniintc/goes-design-system";
import { useState } from "react";
import { useRegistryForm } from "../contexts/RegistryFormContext";
import { AxiosError } from "axios";

interface RegistryListProps {
  registryCode?: string;
  registries?: AssociatedRegistryType[];
}

export const RegistryList: React.FC<RegistryListProps> = ({ registryCode }) => {
  const [inputValue, setInputValue] = useState("");
  const [inputError, setInputError] = useState("");
  const { registries, setRegistries } = useRegistryForm();
  const { mutateAsync: getRegistryByUniqueCode, isPending } =
    useGetRegistryByUniqueCode();

  const handleAddListItem = async () => {
    if (inputValue.trim() === "") return;
    if (inputValue === registryCode) {
      setInputError("El código no puede ser igual al del registro actual.");
      return;
    }
    try {
      const response = await getRegistryByUniqueCode(inputValue.trim());
      setRegistries([...registries, response.data]);
      setInputValue("");
    } catch (error) {
      console.error("Error fetching registry:", error);
      if (error instanceof AxiosError) {
        setInputError(error?.response?.data?.message ?? "");
      }
    }
  };

  const handleRemoveListItem = (index: number) => {
    const newList = [...registries];
    newList.splice(index, 1);
    setRegistries(newList);
  };

  return (
    <div className="flex flex-col gap-2">
      <div>
        <div className="flex items-end group">
          <GroupTextField
            className="w-full"
            label="Registros asociados"
            labelHelperText="Opcional"
            error={inputError}
          >
            <Input
              className="w-full"
              placeholder="Añade registros mediante IDs"
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                setInputError("");
              }}
            />
            <Button
              variant="quiet"
              type="button"
              className="border-neutral!"
              onClick={handleAddListItem}
              disabled={inputValue.trim() === "" || isPending}
              aria-label="Agregar registro asociado"
            >
              <Icon name={isPending ? "spinner-third" : "plus"} type="far" />
            </Button>
          </GroupTextField>
        </div>
      </div>

      {registries.length > 0 && (
        <div className="w-full rounded-lg border border-neutral bg-neutral-object shadow-sm p-1">
          <div className="flex flex-col">
            {registries.map((item, index) => (
              <div
                key={`${item.id}`}
                className="px-2 py-2 flex items-center gap-4"
              >
                <Avatar size="sm" shape="circle">
                  <AvatarFallback color="neutral" shape="circle">
                    <Icon
                      type="fas"
                      name="folder"
                      size="xs"
                      className="text-body-quiet"
                    />
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-body text-sm truncate">
                  <span className="font-semibold">{item.name}</span>{" "}
                  {item.base_entity_name ?? ""}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  type="button"
                  aria-label="Eliminar"
                  onClick={() => handleRemoveListItem(index)}
                >
                  <Icon type="far" name="times" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
